import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type Role = "admin" | "user";

interface AuthState {
	token: string | null;
	role: Role | null;
	isAuthenticated: boolean;
	isAdmin: boolean;
}

interface AuthContextValue extends AuthState {
	login: (password: string) => Promise<{ success: boolean; error?: string }>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = "elo-tracker-auth";

export function AuthProvider({ children }: { children: ReactNode }) {
	const [token, setToken] = useState<string | null>(null);
	const [role, setRole] = useState<Role | null>(null);
	const [loading, setLoading] = useState(true);

	// Verify stored token on mount
	useEffect(() => {
		async function verifyStoredToken() {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (!stored) {
				setLoading(false);
				return;
			}

			try {
				const { token: storedToken } = JSON.parse(stored);
				const res = await fetch("/api/auth/verify", {
					headers: { Authorization: `Bearer ${storedToken}` },
				});

				if (res.ok) {
					const data = await res.json();
					setToken(storedToken);
					setRole(data.role);
				} else {
					localStorage.removeItem(STORAGE_KEY);
				}
			} catch {
				localStorage.removeItem(STORAGE_KEY);
			} finally {
				setLoading(false);
			}
		}

		verifyStoredToken();
	}, []);

	const login = useCallback(async (password: string) => {
		try {
			const res = await fetch("/api/auth/login", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ password }),
			});

			const data = await res.json();

			if (!res.ok) {
				return { success: false, error: data.error || "Login failed" };
			}

			setToken(data.token);
			setRole(data.role);
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: data.token }));

			return { success: true };
		} catch {
			return { success: false, error: "Network error" };
		}
	}, []);

	const logout = useCallback(async () => {
		if (token) {
			try {
				await fetch("/api/auth/logout", {
					method: "POST",
					headers: { Authorization: `Bearer ${token}` },
				});
			} catch {
				// Ignore logout errors
			}
		}

		setToken(null);
		setRole(null);
		localStorage.removeItem(STORAGE_KEY);
	}, [token]);

	// Show nothing while checking stored token
	if (loading) {
		return null;
	}

	const value: AuthContextValue = {
		token,
		role,
		isAuthenticated: !!token,
		isAdmin: role === "admin",
		login,
		logout,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

export function getAuthHeaders(token: string | null): Record<string, string> {
	if (!token) return {};
	return { Authorization: `Bearer ${token}` };
}
